import {appUrl} from '@frontend/AppUrl'
import {trans} from '@frontend/helpers'
import {useUserData} from '@frontend/hooks'
import {
    useAccountFlagsStore,
    useAuthStore,
    useTopMenuStore,
} from '@frontend/stores'
import {
    mdiBell,
    mdiCog,
    mdiHeartOutline,
    mdiLogout,
    mdiMessageReplyText,
} from '@mdi/js'
import {iconProfile} from '@shared/helpers/icons'
import clsx from 'clsx'
import type {MouseEvent} from 'react'
import {useMatch} from 'react-router-dom'
import {FadeIn} from 'swayok-react-mdb-ui-kit/components/Animation/FadeIn'
import {FadeSwitch} from 'swayok-react-mdb-ui-kit/components/Animation/FadeSwitch'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {IconHref} from 'swayok-react-mdb-ui-kit/components/Icon/IconHref'
import {Badge} from 'swayok-react-mdb-ui-kit/components/Typography/Badge'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'

interface Props {
    noLabels?: boolean
    noLogout?: boolean
    noFavorites?: boolean
    noSettings?: boolean
}

// Набор кнопок связанных с личным кабинетом пользователя.
export function CabinetButtons(props: Props) {

    const {
        noLabels,
        noLogout,
        noFavorites,
        noSettings,
    } = props

    const {
        hasNewNotifications,
        hasNewChatMessages,
    } = useAccountFlagsStore()

    const topMenuCabinetMode = useTopMenuStore(
        state => state.cabinetMode
    )
    const topMenuAuthBlock = useTopMenuStore(
        state => state.authBlock
    )
    const initUserAuth = useAuthStore(
        state => state.initUserAuthModal
    )
    const logout = useAuthStore(
        state => state.logout
    )
    const user = useUserData()
    const isLoggedIn: boolean = !!user

    // Определение текущего URL (раздела в кабинете).
    const favoritesUrlMatch = useMatch(appUrl().cabinet.favorites)
    const notificationsUrlMatch = useMatch(appUrl().cabinet.notifications.index)
    const chatsUrlMatch = useMatch(appUrl().cabinet.chats.index)
    const profileUrlMatch = useMatch(appUrl().cabinet.settings.index)

    // Показывает окно входа, если пользователь не авторизован.
    // После успешного входа отправляет пользователя на запрошенный URL.
    const protectedOnClick = (e: MouseEvent<HTMLElement>) => {
        const href: string | null = e.currentTarget.attributes.getNamedItem('href')?.value || null
        if (initUserAuth(href ? () => NavigationService.navigate(href) : null)) {
            e.preventDefault()
            return
        }
    }

    // Кнопка перехода в избранное.
    const favoritesLink = (
        <div className="ps-2 ps-sm-3 overflow-hidden">
            <IconHref
                path={mdiHeartOutline}
                className={clsx(
                    'cabinet-link-icon',
                    favoritesUrlMatch ? 'active' : null
                )}
                tooltip={trans().common.top_navigation.liked_ads}
                href={appUrl().cabinet.favorites}
                onClick={protectedOnClick}
                visible={!noFavorites}
            />
        </div>
    )

    // Кнопки для неавторизованного пользователя.
    const notLoggedInContent = (
        <div className="flex-row-center justify-content-end">
            {topMenuAuthBlock && (
                <>
                    {favoritesLink}
                    <div className="ps-2 ps-sm-3 overflow-hidden">
                        <Button
                            color="link"
                            className="btn-cabinet pe-2 flex-nowrap text-nowrap"
                            hasIcon
                            href={appUrl().cabinet.default}
                            onClick={protectedOnClick}
                            title={noLabels ? trans().common.top_navigation.cabinet : undefined}
                        >
                            {!noLabels && !isLoggedIn && (
                                <span className="d-none d-sm-inline">
                                    {trans().common.top_navigation.cabinet}
                                </span>
                            )}
                            <Icon
                                path={iconProfile}
                                className={noLabels ? '' : 'ms-sm-2'}
                            />
                        </Button>
                    </div>
                </>
            )}
        </div>
    )

    // Кнопки для авторизованного пользователя.
    const loggedInContent = (
        <div className="flex-row-center justify-content-end">
            {(topMenuAuthBlock || topMenuCabinetMode) && (
                <div className="flex-row-center justify-content-end">
                    {favoritesLink}
                    <div className="ps-2 ps-sm-3">
                        <IconHref
                            path={mdiBell}
                            className={clsx(
                                'cabinet-link-icon',
                                notificationsUrlMatch ? 'active' : null
                            )}
                            tooltip={trans().common.top_navigation.notifications}
                            href={appUrl().cabinet.notifications.index}
                            onClick={protectedOnClick}
                        >
                            <FadeIn visible={hasNewNotifications}>
                                <Badge
                                    dot
                                    color="red"
                                />
                            </FadeIn>
                        </IconHref>
                    </div>
                    <div className="ps-2 ps-sm-3">
                        <IconHref
                            path={mdiMessageReplyText}
                            className={clsx(
                                'cabinet-link-icon',
                                chatsUrlMatch ? 'active' : null
                            )}
                            tooltip={trans().common.top_navigation.chats}
                            href={appUrl().cabinet.chats.index}
                            onClick={protectedOnClick}
                            color={hasNewChatMessages ? 'blue' : undefined}
                        >
                            <FadeIn visible={hasNewChatMessages}>
                                <Badge
                                    dot
                                    color="red"
                                />
                            </FadeIn>
                        </IconHref>
                    </div>
                    <FadeSwitch transitionKey={topMenuCabinetMode}>
                        {topMenuCabinetMode ? (
                            <div className={noSettings ? '' : 'ps-2 ps-sm-3'}>
                                <IconHref
                                    path={mdiCog}
                                    className={clsx(
                                        'cabinet-link-icon',
                                        profileUrlMatch ? 'active' : null
                                    )}
                                    tooltip={trans().common.top_navigation.profile}
                                    href={appUrl().cabinet.settings.index}
                                    onClick={protectedOnClick}
                                    visible={!noSettings}
                                />
                            </div>
                        ) : (
                            <div className="ps-2 ps-sm-3">
                                <IconHref
                                    path={iconProfile}
                                    className="cabinet-link-icon"
                                    href={appUrl().cabinet.default}
                                    tooltip={trans().common.top_navigation.cabinet}
                                />
                            </div>
                        )}
                    </FadeSwitch>
                </div>
            )}
            {!noLogout && (
                <div className="ps-2 ps-sm-3">
                    <IconHref
                        path={mdiLogout}
                        className="cabinet-link-icon"
                        tooltip={trans().common.top_navigation.logout}
                        href={appUrl().logout}
                        onClick={e => {
                            e.preventDefault()
                            logout()
                        }}
                    />
                </div>
            )}
        </div>
    )

    return (
        <FadeSwitch transitionKey={isLoggedIn}>
            {isLoggedIn ? loggedInContent : notLoggedInContent}
        </FadeSwitch>
    )
}
